import { useMemo } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import PropTypes from 'prop-types';

import CountryCityFields from './CountryCityFields';
import OptionBox from '../../common-components/OptionBox';
import Select from '../../common-components/Select';
import {
  ADDITIONAL_INFORMATION_CITY_COUNTRY_TYPE,
  ADDITIONAL_INFORMATION_OPTION_BOX_TYPE,
  ADDITIONAL_INFORMATION_YEAR_OF_BIRTH_TYPE,
  SCHOOL_IDS,
  ACADEMIC_IDS,
  ALL_ACADEMIC_EDUCATION,
} from '../data/constants';
import messages from '../messages';

const StepRenderer = ({
  stepData,
  additionalInformation,
  onSelect,
}) => {
  const { formatMessage } = useIntl();

  function useEducationStepOptions(stepData) {
    return useMemo(() => {
  
      if (!['level_of_education', 'academic_degree'].includes(stepData?.fieldKey)) {
        return { backgroundOptions: [], degreeOptions: [] };
      }

      const labelById = Object.fromEntries(stepData.options);

      const backgroundOptions = [

        // academic degree dummy option to go to the next step
        [ALL_ACADEMIC_EDUCATION, formatMessage(messages['additional.information.education.academic'])],

        // practical engineer
        ...(labelById.a ? [['a', labelById.a]] : []),

        // school education (merged -> 'hs')
        ...(SCHOOL_IDS.some((id) => labelById[id])
          ? [
              [
                'hs',
                formatMessage(
                  messages['additional.information.school.education']
                ),
              ],
            ]
          : []),

        // no formal education
        ...(labelById.none
          ? [
              [
                'none',
                formatMessage(
                  messages['additional.information.education.none'],
                  { lineBreak: <br /> }
                ),
              ],
            ]
          : []),
      ];

      const degreeOptions = ACADEMIC_IDS.filter((id) => labelById[id]).map(
        (id) => [id, labelById[id]]
      );

      return { backgroundOptions, degreeOptions };
    }, [stepData, formatMessage]);
  }

  const { backgroundOptions, degreeOptions } = useEducationStepOptions(stepData);

  const renderFields = () => {
    switch (stepData.type) {
      case ADDITIONAL_INFORMATION_OPTION_BOX_TYPE:
        if (stepData.fieldKey === 'level_of_education') {
          return (
              <div className="grid-three-columns">
                {backgroundOptions.map(([value, label]) => (
                  <OptionBox
                    key={label}
                    isActive={
                      additionalInformation[stepData.fieldKey] === value
                    }
                    option={label}
                    onClick={() => onSelect(stepData.fieldKey, value)}
                  />
                ))}
              </div>
          );
        }
        if (stepData.fieldKey === 'academic_degree') {
          return (
            <div className="grid-three-columns">
              {degreeOptions.map(([value, label]) => (
                <OptionBox
                  key={label}
                  isActive={
                    additionalInformation[stepData.fieldKey] === value
                  }
                  option={label}
                  onClick={() => onSelect(stepData.fieldKey, value)}
                />
              ))}
            </div>
          );
        }
        if (stepData.fieldKey === 'gender') {
          return (
            <div className="grid-three-columns">
              {stepData.options.map((option) => (
                <OptionBox
                  key={option[1]}
                  isActive={
                    additionalInformation[stepData.fieldKey] === option[0]
                  }
                  option={option[1]}
                  onClick={() => onSelect(stepData.fieldKey, option[0])}
                />
              ))}
            </div>
          );
        }
      case ADDITIONAL_INFORMATION_YEAR_OF_BIRTH_TYPE:
        const MIN_YEAR_OF_BIRTH = 13
        const date = new Date();
        let current_year = date.getFullYear();
        return (
          <Select
            label={formatMessage(messages['year.of.birth.select.label'])}
            value={additionalInformation[stepData.fieldKey]}
            options={stepData.options
              .slice()
              .filter((option) => Number(option[1]) <= current_year - MIN_YEAR_OF_BIRTH)
              .sort((a, b) => Number(b[1]) - Number(a[1])) // youngest to oldest
              .map((option) => ({
                id: option[0],
                value: option[1],
              }))}
            onChange={(year) => onSelect('year_of_birth', year.id)}
          />
        );
      case ADDITIONAL_INFORMATION_CITY_COUNTRY_TYPE:
        return (
          <CountryCityFields
            additionalInformation={{
              ...additionalInformation,
              country: additionalInformation.country || 'IL',
              city: additionalInformation.city || '',
            }}
            onSelect={onSelect}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-100 flex-center-column step-renderer">
      <div className="step-details flex-center-column">
        {stepData.icon}
        <h1 className="page-title">{stepData.pageTitle}</h1>
      </div>
      {renderFields()}
    </div>
  );
};

StepRenderer.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  stepData: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  additionalInformation: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default StepRenderer;
